import React, { useEffect, useState } from 'react'
import { useI18n } from '../../../i18n/I18nContext'
import { getWorkerOnboardingPolicy, saveWorkerOnboardingPolicy, type WorkerOnboardingPolicyState } from '../../../services/workerOnboardingPolicy'

const labels: Record<string, string> = {
  name: 'Name', phone: 'Phone', skills: 'Skills', availability: 'Availability', emergency: 'Emergency contact',
  languages: 'Languages', work_area: 'Work area', documents: 'Documents', photo: 'Profile photo', resume: 'Resume',
  experience: 'Employment experience', training: 'Training',
  certificate: 'Certificate', license: 'License', training_card: 'Training card', safety_induction: 'Safety induction',
  safety_card: 'Safety card', government_id: 'Government ID', iqama: 'Iqama', passport: 'Passport', national_id: 'National ID',
  driver_license_id: 'Driver license ID', work_permit: 'Work permit', site_access_card: 'Site access card',
  medical_clearance: 'Medical clearance', immigration_document: 'Immigration document', portfolio: 'Portfolio', trade_certificate: 'Trade certificate',
}

export const WorkerOnboardingPolicyPanel: React.FC<{ tenantId: string; canManage: boolean }> = ({ tenantId, canManage }) => {
  const { t } = useI18n()
  const [state, setState] = useState<WorkerOnboardingPolicyState | null>(null)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [reload, setReload] = useState(0)
  useEffect(() => {
    let current = true
    setState(null); setStatus('')
    if (canManage) getWorkerOnboardingPolicy(tenantId).then(result => {
      if (current && result.success && result.tenantId === tenantId) setState(result)
    }).catch(() => { if (current) setStatus('Unable to load onboarding policy.') })
    return () => { current = false }
  }, [tenantId, canManage, reload])
  if (!canManage) return null
  const toggle = (field: 'requiredSteps' | 'requiredDocumentTypes', value: string, checked: boolean) => {
    setState(current => {
      if (!current) return current
      const values = current.policy[field].filter(v => v !== value)
      if (checked) values.push(value)
      const policy = { ...current.policy, [field]: values }
      if (policy.requiredDocumentTypes.length && !policy.requiredSteps.includes('documents')) policy.requiredSteps = [...policy.requiredSteps, 'documents']
      return { ...current, policy }
    })
  }
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!state || state.tenantId !== tenantId) return
    setSaving(true); setStatus('')
    try {
      const result = await saveWorkerOnboardingPolicy(tenantId, state.policy, state.revision)
      if (!result.success || result.tenantId !== tenantId) throw new Error('Unexpected tenant')
      setState(current => current ? { ...current, policy: result.policy, revision: result.revision } : null)
      setStatus('Onboarding policy saved.')
    } catch { setStatus('Unable to save onboarding policy. Reload before trying again.') }
    finally { setSaving(false) }
  }
  return <form className="panel workspace-panel stack" onSubmit={save}>
    <h3>{t('Worker onboarding policy')}</h3>
    <p>{t('Choose required profile information and documents. Collection does not verify identity or grant permission to work. Document approval uses existing reviewer permissions.')}</p>
    {!state ? <p role="status">{status ? '' : t('Loading onboarding policy')}</p> : <>
      <fieldset disabled={saving}><legend>{t('Required profile information')}</legend>
        {state.stepKeys.map(key => <label key={key} className="stack"><span><input type="checkbox" checked={state.policy.requiredSteps.includes(key)} disabled={['name', 'phone'].includes(key) || (key === 'documents' && state.policy.requiredDocumentTypes.length > 0)} onChange={e => toggle('requiredSteps', key, e.target.checked)} /> {t(labels[key] || key)}</span></label>)}
      </fieldset>
      <fieldset disabled={saving}><legend>{t('Required onboarding documents')}</legend>
        {state.documentTypes.map(key => <label key={key} className="stack"><span><input type="checkbox" checked={state.policy.requiredDocumentTypes.includes(key)} onChange={e => toggle('requiredDocumentTypes', key, e.target.checked)} /> {t(labels[key] || key)}</span></label>)}
      </fieldset>
      <button className="btn btn-primary" disabled={saving}>{t('Save onboarding policy')}</button>
    </>}
    <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => setReload(value => value + 1)}>{t('Reload onboarding policy')}</button>
    {status && <p role="status">{t(status)}</p>}
  </form>
}
